import { preloadFont } from "troika-three-text";

const COMMON_SDF_FONT_PRELOAD_CHARACTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789 .,;:!?\"'`~@#$%^&*()-_+=[]{}<>/\\|°\n";
const preloadedSdfFontKeys = new Set();

const FONT_OPTIONS = [
  { label: "Arial", value: "Arial, Helvetica, sans-serif", sdfFontUrl: "/static/data/fonts/arial.ttf" },
  { label: "Arial Black", value: "\"Arial Black\", Impact, Verdana, sans-serif", sdfFontUrl: "/static/data/fonts/arial-black.ttf" },
  { label: "Impact", value: "Impact, \"Arial Black\", Verdana, sans-serif", sdfFontUrl: "/static/data/fonts/impact.ttf" },
  { label: "Verdana", value: "Verdana, Geneva, sans-serif", sdfFontUrl: "/static/data/fonts/verdana.ttf" },
  { label: "Tahoma", value: "Tahoma, Verdana, Segoe, sans-serif", sdfFontUrl: "/static/data/fonts/tahoma.ttf" },
  { label: "Trebuchet MS", value: "\"Trebuchet MS\", \"Lucida Grande\", \"Lucida Sans Unicode\", sans-serif", sdfFontUrl: "/static/data/fonts/trebuchet-ms.ttf" },
  { label: "Times New Roman", value: "\"Times New Roman\", Times, serif", sdfFontUrl: "/static/data/fonts/times-new-roman.ttf" },
  { label: "Georgia", value: "Georgia, serif", sdfFontUrl: "/static/data/fonts/georgia.ttf" },
  { label: "Garamond", value: "Garamond, Baskerville, \"Baskerville Old Face\", serif", sdfFontUrl: "/static/data/fonts/garamond.ttf" },
  { label: "Palatino", value: "\"Palatino Linotype\", \"Book Antiqua\", Palatino, serif", sdfFontUrl: "/static/data/fonts/palatino.ttf" },
  { label: "Courier New", value: "\"Courier New\", Courier, monospace", sdfFontUrl: "/static/data/fonts/courier-new.ttf" },
  { label: "Consolas", value: "Consolas, monaco, monospace", sdfFontUrl: "/static/data/fonts/consolas.ttf" },
  { label: "Comic Sans MS", value: "\"Comic Sans MS\", sans-serif", sdfFontUrl: "/static/data/fonts/comic-sans-ms.ttf" },
  { label: "Brush Script MT", value: "\"Brush Script MT\", cursive", sdfFontUrl: "/static/data/fonts/brush-script.ttf" },
];

const DEFAULT_LABEL_FONT_OPTION = FONT_OPTIONS[0];
const DEFAULT_LABEL_FONT = DEFAULT_LABEL_FONT_OPTION.value;

function normalizeFontValue(font = "") {
  return String(font)
    .replace(/(\d+(?:\.\d+)?)px\s*/i, "")
    .replace(/["']/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function getFontOption(font) {
  const normalizedFont = normalizeFontValue(font);
  return FONT_OPTIONS.find((option) => normalizeFontValue(option.value) === normalizedFont)
    || [...FONT_OPTIONS]
      .sort((a, b) => b.label.length - a.label.length)
      .find((option) => normalizedFont.includes(normalizeFontValue(option.label)));
}

function getSdfFontUrlForFont(font) {
  const normalizedFont = normalizeFontValue(font);
  return FONT_OPTIONS.find((option) => normalizeFontValue(option.value) === normalizedFont)?.sdfFontUrl;
}

function getTextForSdfFontPreload(value) {
  if (value === undefined || value === null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) return value.map(getTextForSdfFontPreload).join("\n");
  if (typeof value !== "object") return "";

  const text = [];
  if (Array.isArray(value.lines)) text.push(getTextForSdfFontPreload(value.lines));
  if (value.richText) text.push(getTextForSdfFontPreload(value.richText));
  if (Object.prototype.hasOwnProperty.call(value, "text")) text.push(getTextForSdfFontPreload(value.text));
  return text.join("\n");
}

function getSdfFontPreloadCharacters(text) {
  return Array.from(new Set(`${COMMON_SDF_FONT_PRELOAD_CHARACTERS}${getTextForSdfFontPreload(text)}`))
    .sort()
    .join("");
}

function preloadMappedSdfFontsForText(text, onPreloadComplete) {
  if (typeof preloadFont !== "function") return [];

  const characters = getSdfFontPreloadCharacters(text);
  return FONT_OPTIONS
    .map((option) => option.sdfFontUrl)
    .filter(Boolean)
    .filter((font) => {
      const preloadKey = `${font}\u0000${characters}`;
      if (preloadedSdfFontKeys.has(preloadKey)) return false;

      preloadedSdfFontKeys.add(preloadKey);
      preloadFont({ font, characters }, () => {
        if (typeof onPreloadComplete === "function") onPreloadComplete(font);
      });
      return true;
    });
}

function resetSdfFontPreloadCacheForTests() {
  preloadedSdfFontKeys.clear();
}

export {
  DEFAULT_LABEL_FONT,
  DEFAULT_LABEL_FONT_OPTION,
  FONT_OPTIONS,
  getFontOption,
  getSdfFontPreloadCharacters,
  getSdfFontUrlForFont,
  normalizeFontValue,
  preloadMappedSdfFontsForText,
  resetSdfFontPreloadCacheForTests,
};
